// MySubmissions — officer-side card listing the caller's recent task
// submissions, with click-to-open detail modal showing task config,
// verifications, evidence, signature, and admin replies.
//
// Spec: docs/specs/location-tasks.md §11.2 — extends /liff/officer/history.
//
// Backend:
//   GET /v1/patrols/me/submissions     (PR P)
//   GET /v1/patrols/submissions/:id    (now returns responses too — PR P)
//
// The page also accepts a ?submission=<id> query param to deep-link
// directly to a specific submission (used by LINE Flex bubbles emitted
// in PR O).

import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CheckCircle2, AlertTriangle, ChevronRight, X, Fingerprint, ScanFace, MapPin,
  FileText, Image as ImageIcon, Upload, PenLine, MessageSquare,
} from "lucide-react";
import { useLiff } from "../../../liff/index.js";
import { Card, CardHeader, EmptyMobile, MobileSpinner, ListRow } from "../../../liff/components/Mobile.js";
import { apiFetch } from "../../../lib/api-client.js";
import { formatTaipeiDateTime } from "../../../lib/time.js";

interface MySubItem {
  id: string;
  task_id: string;
  task_name: string | null;
  assignment_id: string;
  location_id: string | null;
  location_label: string | null;
  status: "in_progress" | "submitted" | "reviewed" | "expired" | "skipped" | string;
  skip_reason: string | null;
  started_at: number;
  submitted_at: number | null;
  reviewed_at: number | null;
}

interface ListResp {
  items: MySubItem[];
  has_more: boolean;
  next_before: string | null;
}

interface Detail {
  submission: MySubItem & {
    text_response: string | null;
    photo_r2_keys: string | null;
    file_r2_keys: string | null;
    signature_r2_key: string | null;
    verify_device_assertion_at: number | null;
    verify_face_liveness_at: number | null;
    verify_face_match_score: number | null;
    verify_face_photo_r2_key: string | null;
    verify_face_liveness_action: string | null;
    verify_geo_at: number | null;
    verify_geo_distance_m: number | null;
    verify_geo_lat: number | null;
    verify_geo_lon: number | null;
  } | null;
  task: {
    name: string;
    description_short: string | null;
    requires_text: number; requires_camera: number; requires_file: number;
    requires_signature: number;
    verify_device: number; verify_face: number; verify_geo: number;
  } | null;
  responses: Array<{
    id: string; responder_user_id: string; body: string;
    sent_to_line_at: number | null; line_message_id: string | null;
    created_at: number;
  }>;
}

const STATUS_LABEL: Record<string, string> = {
  in_progress: "進行中",
  submitted: "已提交",
  reviewed: "已審核",
  expired: "已過期",
  skipped: "已跳過",
};

export default function MySubmissions(): React.ReactElement {
  const { session } = useLiff();
  const [params, setParams] = useSearchParams();
  const deepLinked = params.get("submission");

  const [items, setItems] = useState<MySubItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(deepLinked);

  useEffect(() => {
    if (!session?.session_token) return;
    apiFetch(`/v1/patrols/me/submissions?limit=30`, {}, { jwt: session.session_token })
      .then(async (r) => {
        if (r.ok) {
          const j = (await r.json()) as ListResp;
          setItems(j.items);
        } else if (r.status === 404 || r.status === 501) {
          setItems([]);
        } else {
          setError(`HTTP ${r.status}`);
        }
      })
      .catch((e) => setError((e as Error).message));
  }, [session?.session_token]);

  const closeModal = useCallback(() => {
    setOpenId(null);
    if (deepLinked) {
      const next = new URLSearchParams(params);
      next.delete("submission");
      setParams(next, { replace: true });
    }
  }, [deepLinked, params, setParams]);

  if (error) {
    return (
      <Card>
        <CardHeader title="近期任務提交" />
        <p className="text-sm text-danger">{error}</p>
      </Card>
    );
  }
  if (items === null) {
    return (
      <Card>
        <CardHeader title="近期任務提交" />
        <MobileSpinner message="載入中" />
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader title="近期任務提交" hint={`${items.length} 筆`} />
        {items.length === 0 ? (
          <EmptyMobile title="尚無任務提交" body="完成第一個任務精靈後會出現在這裡。" />
        ) : (
          <ul className="space-y-2">
            {items.map((it) => (
              <li key={it.id}>
                <button
                  onClick={() => setOpenId(it.id)}
                  className="w-full text-left rounded-xl bg-bg-raised hover:bg-bg-hover transition px-3 py-2.5 flex items-center justify-between gap-2"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{it.task_name ?? it.task_id.slice(0, 10)}</p>
                    <p className="text-2xs text-text-muted truncate">
                      {it.location_label ?? "—"} · {fmtTime(it.submitted_at ?? it.started_at)}
                    </p>
                  </div>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${pillTone(it.status)}`}>
                    {STATUS_LABEL[it.status] ?? it.status}
                  </span>
                  <ChevronRight className="h-4 w-4 text-text-muted shrink-0" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {openId && <DetailModal id={openId} onClose={closeModal} />}
    </>
  );
}

function DetailModal({ id, onClose }: { id: string; onClose: () => void }): React.ReactElement {
  const { session } = useLiff();
  const [data, setData] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session?.session_token) return;
    apiFetch(`/v1/patrols/submissions/${encodeURIComponent(id)}`, {}, { jwt: session.session_token })
      .then(async (r) => {
        if (r.ok) setData((await r.json()) as Detail);
        else setError(`HTTP ${r.status}`);
      })
      .catch((e) => setError((e as Error).message));
  }, [session?.session_token, id]);

  return (
    <div className="fixed inset-0 z-40 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="w-full sm:max-w-lg bg-bg-base rounded-t-2xl sm:rounded-2xl border border-border max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 z-10 backdrop-blur bg-bg-base/90 border-b border-border-subtle px-4 py-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold truncate">{data?.task?.name ?? "任務詳情"}</h3>
          <button onClick={onClose} className="p-1 text-text-muted hover:text-text-primary"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          {error && <p className="text-xs text-danger inline-flex items-start gap-1"><AlertTriangle className="h-3.5 w-3.5 mt-0.5" />{error}</p>}
          {!data ? <MobileSpinner /> : <DetailBody d={data} />}
        </div>
      </div>
    </div>
  );
}

function DetailBody({ d }: { d: Detail }): React.ReactElement {
  if (!d.submission) return <p className="text-sm text-text-muted">找不到紀錄</p>;
  const s = d.submission;
  const t = d.task;
  const photos = parseR2Keys(s.photo_r2_keys);
  const files = parseR2Keys(s.file_r2_keys);

  return (
    <>
      <Card>
        <CardHeader title="狀態" hint={STATUS_LABEL[s.status] ?? s.status} />
        <p className="text-2xs text-text-muted">
          開始：{fmtTime(s.started_at)}
          {s.submitted_at ? <> · 提交：{fmtTime(s.submitted_at)}</> : null}
        </p>
        {s.skip_reason && (
          <p className="text-xs text-warning bg-warning/10 rounded p-2 mt-2 whitespace-pre-wrap">跳過：{s.skip_reason}</p>
        )}
      </Card>

      {/* Step 1 驗證列表 — 位置（geo）chip 暫時隱藏，NTAG 已綁實體位置。
          device + face 條目保留；之後要開回 geo 只要把下方 `t.verify_geo`
          那個 VRow 從 comment 解出來，並把外層 `|| t.verify_geo === 1`
          一起 uncomment 即可。 */}
      {t && (t.verify_device === 1 || t.verify_face === 1 /* || t.verify_geo === 1 */) && (
        <Card>
          <CardHeader title="Step 1 驗證" />
          <ul className="space-y-1.5 text-xs">
            {t.verify_device === 1 && (
              <VRow icon={<Fingerprint className="h-3.5 w-3.5" />} label="裝置 (passkey)" done={!!s.verify_device_assertion_at} />
            )}
            {t.verify_face === 1 && (
              <VRow
                icon={<ScanFace className="h-3.5 w-3.5" />}
                label={`人臉${s.verify_face_match_score != null ? ` (${(s.verify_face_match_score * 100).toFixed(0)}/100)` : ""}`}
                done={!!s.verify_face_liveness_at}
              />
            )}
            {/*
            {t.verify_geo === 1 && (
              <VRow
                icon={<MapPin className="h-3.5 w-3.5" />}
                label={`位置${s.verify_geo_distance_m != null ? ` (${s.verify_geo_distance_m.toFixed(1)} m)` : ""}`}
                done={!!s.verify_geo_at}
              />
            )}
            */}
          </ul>
        </Card>
      )}

      {t && (t.requires_text === 1 || t.requires_camera === 1 || t.requires_file === 1) && (
        <Card>
          <CardHeader title="Step 2 證據" />
          {s.text_response && (
            <div className="mb-2">
              <p className="text-2xs uppercase text-text-muted mb-1 inline-flex items-center gap-1"><FileText className="h-3 w-3" />文字</p>
              <p className="text-xs whitespace-pre-wrap rounded bg-bg-raised p-2">{s.text_response}</p>
            </div>
          )}
          {photos.length > 0 && (
            <div className="mb-2">
              <p className="text-2xs uppercase text-text-muted mb-1 inline-flex items-center gap-1"><ImageIcon className="h-3 w-3" />照片 ({photos.length})</p>
              <div className="grid grid-cols-3 gap-1.5">
                {photos.map((k) => <Thumb key={k} r2Key={k} />)}
              </div>
            </div>
          )}
          {files.length > 0 && (
            <div>
              <p className="text-2xs uppercase text-text-muted mb-1 inline-flex items-center gap-1"><Upload className="h-3 w-3" />檔案 ({files.length})</p>
              <ul className="space-y-1">
                {files.map((k) => (
                  <li key={k} className="text-xs font-mono text-text-secondary truncate">{k.split("/").pop()}</li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}

      {t && t.requires_signature === 1 && (
        <Card>
          <CardHeader title="Step 3 簽名" hint={s.signature_r2_key ? "完成" : "未簽"} />
          {s.signature_r2_key
            ? <Thumb r2Key={s.signature_r2_key} className="rounded bg-bg-raised w-full max-w-md" />
            : <p className="text-xs text-text-muted">未簽名</p>}
        </Card>
      )}

      <Card>
        <CardHeader title="管理員回覆" hint={`${d.responses.length} 則`} />
        {d.responses.length === 0 ? (
          <p className="text-xs text-text-muted">尚無回覆。如有疑問可請主管在後台留言。</p>
        ) : (
          <ul className="space-y-2">
            {d.responses.map((r) => (
              <li key={r.id} className="rounded bg-bg-raised p-2.5 text-xs space-y-1">
                <p className="text-2xs text-text-muted inline-flex items-center gap-1">
                  <MessageSquare className="h-3 w-3" />
                  {fmtTime(r.created_at)}
                  {r.sent_to_line_at ? " · 已推 LINE" : ""}
                </p>
                <p className="whitespace-pre-wrap">{r.body}</p>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}

function VRow({ icon, label, done }: { icon: React.ReactNode; label: string; done: boolean }): React.ReactElement {
  return (
    <li className="flex items-center justify-between gap-2">
      <span className="inline-flex items-center gap-1.5 text-text-secondary">{icon}{label}</span>
      <span className={`text-[10px] font-semibold ${done ? "text-success" : "text-warning"}`}>
        {done ? <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />完成</span> : "未完成"}
      </span>
    </li>
  );
}

function pillTone(status: string): string {
  switch (status) {
    case "submitted": return "bg-warning/15 text-warning";
    case "reviewed":  return "bg-success/15 text-success";
    case "expired":   return "bg-danger/15 text-danger";
    case "skipped":   return "bg-danger/15 text-danger";
    case "in_progress": return "bg-accent/15 text-accent";
    default:          return "bg-bg-raised text-text-secondary";
  }
}

function fmtTime(ms: number | null): string {
  return formatTaipeiDateTime(ms);
}

function parseR2Keys(s: string | null): string[] {
  if (!s) return [];
  try { const v = JSON.parse(s); return Array.isArray(v) ? v.filter((x) => typeof x === "string") : []; }
  catch { return []; }
}

function Thumb({ r2Key, className = "rounded aspect-square object-cover w-full" }: { r2Key: string; className?: string }): React.ReactElement {
  const { session } = useLiff();
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!session?.session_token) return;
    let revoke: string | null = null;
    void apiFetch(`/v1/patrols/evidence/${r2Key}`, {}, { jwt: session.session_token })
      .then(async (r) => r.ok ? r.blob() : null)
      .then((blob) => { if (blob) { revoke = URL.createObjectURL(blob); setSrc(revoke); } });
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [r2Key, session?.session_token]);
  if (!src) return <div className={`${className} animate-pulse bg-bg-hover`} />;
  return <img src={src} alt="" className={className} />;
}
